import { useEffect, useMemo, useState } from "react";

import { fetchCompanyRatingConfiguration } from "@/services/adminService";
import { resolveScale } from "@/lib/ratingScale";

/**
 * Shared access to the company's `rating_config` for admin screens.
 *
 * Performance bands are anchored to `base_rating`, so classifying a rating now
 * needs BOTH base and max. Previously each screen sourced only the max, and from
 * whatever endpoint happened to be nearby — `EmployeeList` read it off the
 * seat-limit response, `ViewEmployeeDetails` had nothing at all. That is how the
 * band tables drifted apart in the first place.
 *
 * Fetching base and max together, from one place, makes the failure mode that
 * caused this bug structurally impossible: a screen either has the whole scale
 * or none of it, never half.
 *
 * The full config (not just base/max) is returned because the penalty and bonus
 * figures are also rendered on these screens, and were hardcoded to the legacy
 * defaults.
 */

/**
 * `rating_config` is per-company and only changes from the Settings screen, so
 * one fetch per session is enough. The PROMISE is cached rather than the value,
 * so screens mounting concurrently share a single in-flight request.
 */
let inFlight = null;

/** Call after a successful rating-config save so the session picks up the change. */
export const invalidateRatingConfig = () => {
  inFlight = null;
};

/**
 * @param {object} [fallback] - a config-ish object to use until the fetch lands
 *   (or if it fails). `EmployeeList` passes the seat-limit payload, which
 *   already carries the scale and is not subscription-gated, so the bands are
 *   right on first paint rather than after a round trip.
 */
export default function useRatingConfig(fallback) {
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;

    if (!inFlight) {
      // A failure must not break the screen — GET /admin/ratings sits behind
      // requireActiveSubscription, so a lapsed company legitimately 403s here.
      inFlight = fetchCompanyRatingConfiguration().catch(() => null);
    }

    inFlight.then((value) => {
      if (!alive) return;
      setConfig(value);
      setReady(true);
    });

    return () => {
      alive = false;
    };
  }, []);

  // Frozen and memoised, so it is safe to put in dependency arrays.
  const scale = useMemo(
    () => resolveScale(config || fallback),
    [config, fallback],
  );

  return { config, scale, ready };
}
